import axios from "axios";
import DOMPurify from "dompurify";
import React, { useEffect, useState } from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { useLocation } from "react-router-dom";

import Footer from "./Footer";
import TopBar from "./Topbar";

const topics = ["Topic 1", "Topic 2", "Topic 3", "Topic 4"];

const FAQpublic = () => {
    const [faqs, setFaqs] = useState([]);
    const location = useLocation();

    // Determine the display mode based on the route
    const isPublicRoute =
        location.pathname.startsWith("/faq-public") ||
        location.pathname.startsWith("/faq/public/");

    // For dashboard routes, hide TopBar/Footer and show sidebar
    // For public routes, show TopBar/Footer and hide sidebar
    const showTopBarAndFooter = isPublicRoute;

    // Get role from path for both dashboard and public routes
    const getRoleFromPath = () => {
        // Handle dashboard routes: /dashboard/faq/investors, /dashboard/faq/entrepreneurs, /dashboard/faq/general, /dashboard/faq/glossary
        const dashboardMatch = location.pathname.match(
            /dashboard\/faq\/(entrepreneurs?|investors?|general|glossary)/i,
        );
        if(dashboardMatch) {
            if(dashboardMatch[1].toLowerCase().startsWith("investor"))
                return "investor";
            if(dashboardMatch[1].toLowerCase().startsWith("entrepreneur"))
                return "entrepreneur";
            if(dashboardMatch[1].toLowerCase() === "general")
                return "general";
            if(dashboardMatch[1].toLowerCase() === "glossary")
                return "glossary";
        }

        // Handle public routes: /faq/public/investors, /faq/public/entrepreneurs, /faq/public/general, /faq/public/glossary
        const publicMatch = location.pathname.match(
            /faq\/public\/(entrepreneurs?|investors?|general|glossary)/i,
        );
        if(publicMatch) {
            if(publicMatch[1].toLowerCase().startsWith("investor"))
                return "investor";
            if(publicMatch[1].toLowerCase().startsWith("entrepreneur"))
                return "entrepreneur";
            if(publicMatch[1].toLowerCase() === "general") return "general";
            if(publicMatch[1].toLowerCase() === "glossary") return "glossary";
        }

        return "general";
    };
    const [selectedRole, setSelectedRole] = useState(getRoleFromPath());
    const [expandedId, setExpandedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [glossaryLetter, setGlossaryLetter] = useState("");
    const [currentTopic] = useState(topics[0]);
    const [popular, setPopular] = useState([]);
    const [pendingExpandId, setPendingExpandId] = useState(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [hasGeneralFAQ, setHasGeneralFAQ] = useState(false);

    useEffect(() => {
        if(location.hash === "#contact-us") {
            const el = document.getElementById("contact-us");
            if(el) {
                el.scrollIntoView({ behavior: "smooth" });
            }
        }

        // If role changes in path, update selectedRole
        const pathRole = getRoleFromPath();
        if(pathRole && pathRole !== selectedRole) setSelectedRole(pathRole);

        // Fetch general FAQ on component mount to check if any exist
        axios
            .get(
                `${process.env.REACT_APP_BACKEND_URL}/api/faq/public/general`,
            )
            .then((res) => {
                setHasGeneralFAQ(res.data && res.data.length > 0);
            })
            .catch(() => {
                setHasGeneralFAQ(false);
            });
    }, [location]);

    // Redirect from general FAQ to entrepreneur if no general FAQs exist
    useEffect(() => {
        if(!hasGeneralFAQ && selectedRole === "general") {
            setSelectedRole("entrepreneur");
        }
    }, [hasGeneralFAQ, selectedRole]);

    // Separate effect to handle FAQ anchor links after FAQs are loaded
    useEffect(() => {
        if(
            location.hash &&
            location.hash.startsWith("#faq-") &&
            faqs.length > 0
        ) {
            const faqIdStr = location.hash.substring(5);
            const faqId = parseInt(faqIdStr);
            if(!isNaN(faqId)) {
                const match = faqs.find((faq) => faq.id === faqId);
                if(match) {
                    setPendingExpandId(faqId);
                }
            }
        }
    }, [location.hash, faqs]);

    useEffect(() => {
        let url;
        if(
            selectedRole === "entrepreneur" ||
            selectedRole === "investor" ||
            selectedRole === "general"
        ) {
            // Use plural in API path for SEO-friendly URLs, with /api/faq/public prefix
            url = `${process.env.REACT_APP_BACKEND_URL}/api/faq/public/${
                selectedRole === "entrepreneur"
                    ? "entrepreneurs"
                    : selectedRole === "investor"
                      ? "investors"
                      : "general"
            }`;
        } else {
            url = `${process.env.REACT_APP_BACKEND_URL}/api/faq/${selectedRole}`;
        }
        axios
            .get(url)
            .then((res) => {
                const seen = new Set();
                const deduped = res.data.filter((faq) => {
                    const key = `${faq.question
                        .trim()
                        .toLowerCase()}|${faq.role.toLowerCase()}`;
                    if(seen.has(key)) {
                        return false;
                    }
                    seen.add(key);
                    return true;
                });
                setFaqs(deduped);
                setExpandedId(null);
            })
            .catch(() => {
                setFaqs([]);
            });
    }, [selectedRole]);

    // Fetch popular faqs for the current topic and role
    useEffect(() => {
        axios
            .get(
                `${
                    process.env.REACT_APP_BACKEND_URL
                }/api/faq/popular/${selectedRole}/${encodeURIComponent(
                    currentTopic,
                )}`,
            )
            .then((res) => setPopular(res.data))
            .catch(() => setPopular([]));
    }, [selectedRole, currentTopic]);
    const toggleAnswer = (id) => {
        if(expandedId !== id) {
            // Only increment hits when expanding (not collapsing)
            axios
                .post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/faq/increment/${id}`,
                )
                .catch(() => {});
        }
        setExpandedId(expandedId === id ? null : id);
    };

    const filteredFaqs = faqs.filter((faq) => {
        if(selectedRole === "glossary") {
            if(glossaryLetter) {
                return (
                    faq.question[0].toLowerCase() ===
                    glossaryLetter.toLowerCase()
                );
            }
            return (
                faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return (
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    useEffect(() => {
        if(pendingExpandId && faqs.length > 0) {
            const match = faqs.find((faq) => faq.id === pendingExpandId);
            if(match) {
                setExpandedId(pendingExpandId);

                const scrollToFAQ = () => {
                    const el = document.getElementById(
                        `faq-${pendingExpandId}`,
                    );
                    if(el) {
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    } else {
                        setTimeout(scrollToFAQ, 100);
                    }
                };

                setTimeout(scrollToFAQ, 150);
            }
            setPendingExpandId(null);
        }
    }, [pendingExpandId, faqs]);

    return (
        <>
            {/* Show TopBar only for public routes */}
            {showTopBarAndFooter && <TopBar />}

            <section className="py-12 px-6 max-w-7xl mx-auto">
                <div className="flex items-center justify-center mb-8 relative">
                    <div className="flex items-center">
                        <h2
                            className="text-center text-3xl md:text-4xl font-semibold text-cyan-900"
                            style={{ fontFamily: "Poppins" }}
                        >
                            EDUCATION CENTER
                        </h2>

                        {/* Mobile Menu - positioned to the right of title with gap */}
                        <div className="md:hidden ml-4">
                            <button
                                onClick={() =>
                                    setShowMobileMenu(!showMobileMenu)
                                }
                                className="p-2 hover:bg-gray-100 rounded-lg transition"
                                style={{ fontFamily: "Poppins" }}
                            >
                                <div className="flex flex-col space-y-1">
                                    <div className="w-1 h-1 bg-cyan-900 rounded-full"></div>
                                    <div className="w-1 h-1 bg-cyan-900 rounded-full"></div>
                                    <div className="w-1 h-1 bg-cyan-900 rounded-full"></div>
                                </div>
                            </button>

                            {showMobileMenu && (
                                <div className="absolute top-full right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-[200px]">
                                    {hasGeneralFAQ && (
                                        <button
                                            onClick={() => {
                                                setSelectedRole("general");
                                                setShowMobileMenu(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 hover:bg-gray-100 first:rounded-t-lg ${
                                            selectedRole === "general"
                                                ? "bg-navy text-paper"
                                                : "text-navy"
                                            }`}
                                            style={{ fontFamily: "Poppins" }}
                                        >
                                            GENERAL FAQ
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            setSelectedRole("entrepreneur");
                                            setShowMobileMenu(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 hover:bg-gray-100 ${
                                            !hasGeneralFAQ ? "first:rounded-t-lg" : "border-t border-gray-200"
                                        } ${
                                            selectedRole === "entrepreneur"
                                                ? "bg-navy text-paper"
                                                : "text-navy"
                                        }`}
                                        style={{ fontFamily: "Poppins" }}
                                    >
                                        ENTREPRENEUR FAQ
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedRole("investor");
                                            setShowMobileMenu(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 hover:bg-gray-100 border-t border-gray-200 ${
                                            selectedRole === "investor"
                                                ? "bg-navy text-paper"
                                                : "text-navy"
                                        }`}
                                        style={{ fontFamily: "Poppins" }}
                                    >
                                        INVESTOR FAQ
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedRole("glossary");
                                            setShowMobileMenu(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 hover:bg-gray-100 border-t border-gray-200 last:rounded-b-lg ${
                                            selectedRole === "glossary"
                                                ? "bg-navy text-paper"
                                                : "text-navy"
                                        }`}
                                        style={{ fontFamily: "Poppins" }}
                                    >
                                        GLOSSARY
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Search Bar - moved above tabs */}
                <div className="flex justify-center mb-6">
                    <div className="relative w-full max-w-[300px] flex items-center">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#D4AF37] text-lg">
                            <i className="fas fa-search"></i>
                        </span>
                        <input
                            type="text"
                            placeholder="SEARCH"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border-2 border-navy rounded-xl pl-10 pr-4 py-2 text-navy font-semibold placeholder-[#D4AF37] italic focus:outline-none bg-white"
                            style={{
                                fontFamily: "Poppins, sans-serif",
                                fontStyle: "italic",
                            }}
                        />
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex justify-center gap-2 mb-6 flex-wrap">
                    {/* Desktop View - Full Buttons */}
                    <div className="hidden md:flex gap-2">
                        {hasGeneralFAQ && (
                            <button
                                onClick={() => setSelectedRole("general")}
                                className={`px-8 py-2 rounded-xl font-semibold ${
                                    selectedRole === "general"
                                        ? "bg-navy text-paper"
                                        : "border border-navy bg-paper text-navy hover:bg-navy hover:text-paper transition"
                                }`}
                                style={{ fontFamily: "Poppins" }}
                            >
                                GENERAL FAQ
                            </button>
                        )}

                        <button
                            onClick={() => setSelectedRole("entrepreneur")}
                            className={`px-8 py-2 rounded-xl font-semibold ${
                                selectedRole === "entrepreneur"
                                    ? "bg-navy text-paper"
                                    : "border border-navy bg-paper text-navy hover:bg-navy hover:text-paper transition"
                            }`}
                            style={{ fontFamily: "Poppins" }}
                        >
                            ENTREPRENEUR FAQ
                        </button>

                        <button
                            onClick={() => setSelectedRole("investor")}
                            className={`px-8 py-2 rounded-xl font-semibold ${
                                selectedRole === "investor"
                                    ? "bg-navy text-paper"
                                    : "border border-navy bg-paper text-navy hover:bg-navy hover:text-paper transition"
                            }`}
                            style={{ fontFamily: "Poppins" }}
                        >
                            INVESTOR FAQ
                        </button>

                        <button
                            onClick={() => setSelectedRole("glossary")}
                            className={`px-8 py-2 rounded-xl font-semibold ${
                                selectedRole === "glossary"
                                    ? "bg-navy text-paper"
                                    : "border border-navy bg-paper text-navy hover:bg-navy hover:text-paper transition"
                            }`}
                            style={{ fontFamily: "Poppins" }}
                        >
                            GLOSSARY
                        </button>
                    </div>
                </div>

                {/* A-Z Filter for Glossary */}
                {selectedRole === "glossary" && (
                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                        <button
                            type="button"
                            onClick={() => {
                                setGlossaryLetter("");
                                setSearchTerm("");
                            }}
                            className={`border px-3 py-1 rounded-md text-sm ${
                                !glossaryLetter
                                    ? "border-navy bg-navy text-paper"
                                    : "border-navy bg-paper text-navy hover:bg-navy hover:text-paper"
                            }`}
                        >
                            All
                        </button>
                        {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((char) => (
                            <button
                                key={char}
                                onClick={() => {
                                    setGlossaryLetter(char);
                                    setSearchTerm("");
                                }}
                                className={`border px-3 py-1 rounded-md text-sm ${
                                    glossaryLetter === char
                                        ? "border-navy bg-navy text-paper"
                                        : "border-navy text-navy hover:bg-navy hover:text-paper"
                                }`}

                            >
                                {char}
                            </button>
                        ))}
                    </div>
                )}

                {/* FAQ Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-6 w-full">
                    {selectedRole === "glossary" ? (
                        <div className="col-span-1 md:col-span-4 lg:col-span-6">
                            {/* Glossary view: full width, no left column */}
                            <div className="bg-white p-4 rounded-lg w-full min-h-[300px] max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-navy scrollbar-track-[#f5f6f8]">
                                {/* Render glossary A-Z filter and entries here */}
                                <div className="space-y-6 pr-2">
                                    {filteredFaqs.length > 0 ? (
                                        filteredFaqs.map((faq) => {
                                            const isExpanded =
                                                expandedId === faq.id;
                                            let raw;
                                            if(
                                                !isExpanded &&
                                                faq.answer.length > 231
                                            ) {
                                                raw =
                                                    faq.answer.slice(0, 231) +
                                                    "...";
                                            } else {
                                                raw = faq.answer;
                                            }
                                            return (
                                                <div
                                                    key={faq.id}
                                                    className={`border-b pb-4 relative${
                                                        isExpanded
                                                            ? " bg-[#C2D7E2] rounded-lg"
                                                            : ""
                                                    }`}
                                                    id={`faq-${faq.id}`}
                                                >
                                                    <div className="ml-3">
                                                        <h5 className="font-bold text-cyan-900 text-lg mb-2">
                                                            {faq.question}
                                                        </h5>
                                                        <div
                                                            className="text-gray-700 text-sm leading-relaxed"
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    raw,
                                                                ),
                                                            }}
                                                        />
                                                    </div>
                                                    {faq.answer.length > 231 && (
                                                        <div
                                                            onClick={() =>
                                                                toggleAnswer(faq.id)
                                                            }
                                                            className="text-[#D4AF37] font-bold text-xl pr-2 mt-2 absolute right-0 top-0 cursor-pointer"
                                                        >
                                                            {isExpanded ? (
                                                                <FaChevronDown />
                                                            ) : (
                                                                <FaChevronRight />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="text-gray-500 italic">
                                            No glossary entries found.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="col-span-1 md:col-span-1 lg:col-span-2">
                                <div className="mb-2">
                                    <h4
                                        className="font-bold text-cyan-900 text-base"
                                        style={{ fontFamily: "Poppins" }}
                                    >
                                        Welcome to the AQi Education Center
                                    </h4>
                                    <p className="text-gray-800 text-sm mb-3">
                                        This portal provides answers to the
                                        questions investors most often search
                                        for when researching how to invest in
                                        women-led startups, Regulation
                                        Crowdfunding, and AQi.
                                    </p>
                                    <p className="text-gray-800 text-sm mb-3">
                                        AQi is a neutral platform: issuers post
                                        their required Reg CF disclosures, and
                                        investors review them here. We
                                        don&apos;t solicit investments, make
                                        recommendations, or provide investment,
                                        legal, or tax advice. Always read the
                                        full Form C and offering documents — and
                                        consider talking to a licensed advisor —
                                        before investing.
                                    </p>
                                    <p className="text-gray-800 text-sm">
                                        If you don’t see your questions
                                        answered, email us at{" "}
                                        <a
                                            href="mailto:contact@aequitasinvest.com"
                                            className="text-cyan-800 underline"
                                        >
                                            contact@aequitasinvest.com
                                        </a>{" "}
                                        and we’ll get back to you shortly.
                                    </p>
                                </div>
                                {popular.length > 0 && (
                                    <div className="mt-8">
                                        <h5
                                            className="font-semibold text-cyan-900 mb-2 text-base"
                                            style={{ fontFamily: "Poppins" }}
                                        >
                                            POPULAR SEARCHES
                                        </h5>
                                        <ul className="list-none ml-0 text-sm text-cyan-800 space-y-1">
                                            {popular.map(({ id, question }) => (
                                                <li
                                                    key={id}
                                                    className="truncate cursor-pointer hover:underline"
                                                    onClick={() => {
                                                        setSearchTerm("");
                                                        setGlossaryLetter("");
                                                        setPendingExpandId(id);
                                                        axios
                                                            .post(
                                                                `${process.env.REACT_APP_BACKEND_URL}/api/faq/increment/${id}`,
                                                            )
                                                            .catch(() => {});
                                                    }}
                                                >
                                                    {question}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <div className="col-span-1 md:col-span-3 lg:col-span-4 min-h-[300px] max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-navy scrollbar-track-[#f5f6f8]">
                                <div className="space-y-6 pr-2">
                                    {filteredFaqs.length > 0 ? (
                                        filteredFaqs.map((faq) => {
                                            const isExpanded =
                                                expandedId === faq.id;
                                            // Show up to 231 chars, then truncate
                                            let raw;
                                            if(
                                                !isExpanded &&
                                                faq.answer.length > 231
                                            ) {
                                                raw =
                                                    faq.answer.slice(0, 231) +
                                                    "...";
                                            } else {
                                                raw = faq.answer;
                                            }
                                            return (
                                                <div
                                                    key={faq.id}
                                                    className={`border-b pb-4 relative${
                                                        isExpanded
                                                            ? " bg-[#C2D7E2] rounded-lg"
                                                            : ""
                                                    }`}
                                                    id={`faq-${faq.id}`}
                                                >
                                                    <div className="ml-3">
                                                        <h5 className="font-bold text-cyan-900 text-lg mb-2">
                                                            {faq.question}
                                                        </h5>
                                                        <div
                                                            className="text-gray-700 text-sm leading-relaxed"
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    raw,
                                                                ),
                                                            }}
                                                        />
                                                    </div>
                                                    {faq.answer.length > 231 && (
                                                        <div
                                                            onClick={() =>
                                                                toggleAnswer(faq.id)
                                                            }
                                                            className="text-[#D4AF37] font-bold text-xl pr-2 mt-2 absolute right-0 top-0 cursor-pointer"
                                                        >
                                                            {isExpanded ? (
                                                                <FaChevronDown />
                                                            ) : (
                                                                <FaChevronRight />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="text-gray-500 italic">
                                            No FAQs found.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                {/* Contact - Show only for public routes */}
                {showTopBarAndFooter && (
                    <div className="w-full flex flex-col items-center justify-center mt-12 space-y-4 text-center">
                        <div id="contact-us" className="mt-12">
                            <p className="text-gray-800 text-sm text-center">
                                If you have more queries, then{" "}
                                <a
                                    href={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/contact-us`}
                                    className="text-navy-2 font-semibold underline"
                                >
                                    Contact Us.
                                </a>
                            </p>
                        </div>
                    </div>
                )}
            </section>

            {/* Show Footer only for public routes */}
            {showTopBarAndFooter && <Footer />}
        </>
    );
};

export default FAQpublic;
